import React, { useEffect, useState } from 'react';
import { Share2, Copy, Check, Mail, MessageCircle, Send, Linkedin } from 'lucide-react';
import { GenerateInviteResponseDTO } from '../Types/AdminType';
import { Alert, AlertProps } from '../../Shared/Utils/Alert';


interface SocialShareComponentProps {
  inviteData: GenerateInviteResponseDTO;
  message?: string;
  status?: string;
}

const SocialShareComponent: React.FC<SocialShareComponentProps> = ({ inviteData,message, status }) => {

  const [alert, setAlert] = useState<AlertProps | null>(null);

  const [copied, setCopied] = useState(false);
  
  // Destructure your DTO fields
  const { inviteUrl, inviteCode, expiryAt } = inviteData;

  // const shareText = `Join Me with Reverse Job Portal using code: ${inviteCode}`;
  // const fullMessage = `${shareText} \nLink: ${inviteUrl}`;

const shareText = `
🚀 *Join Reverse Job Portal* 🚀

✨ *Why Join?*
👉 Build Professional Connections
👉 Explore Better Opportunities
👉 Grow Your Career Faster

🎟️ *Exclusive Invite Code:* ${inviteCode}
`;

const fullMessage = `${shareText}

🔗 *Register Here:*
${inviteUrl}`;

  // 1. Native Share Handler (Mobile/Modern Browsers)
  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Invitation',
          text: shareText,
          url: inviteUrl,
        });
      } catch (err) {
        console.log("Share cancelled or failed", err);
      }
    } else {
      // Fallback: Copy to clipboard if native share is missing
      copyToClipboard();
    }
  };

  // 2. Copy to Clipboard Handler
  const copyToClipboard = () => {
    navigator.clipboard.writeText(inviteUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // 3. Social Media Link Templates
  const socials = [
    {
      name: 'WhatsApp',
      icon: <MessageCircle className="w-5 h-5" />,
      link: `https://wa.me/?text=${encodeURIComponent(fullMessage)}`,
      color: 'hover:bg-slate-800 text-green-400'
    },
    {
      name: 'Telegram',
      icon: <Send className="w-5 h-5" />,
      link: `https://t.me/share/url?url=${encodeURIComponent(inviteUrl)}&text=${encodeURIComponent(shareText)}`,
      color: 'hover:bg-slate-800 text-cyan-400'
    },
    {
      name: 'LinkedIn',
      icon: <Linkedin className="w-5 h-5" />,
      link: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(inviteUrl)}`,
      color: 'hover:bg-slate-800 text-blue-400'
    },
    {
      name: 'Email',
      icon: <Mail className="w-5 h-5" />,
      link: `mailto:?subject=You're Invited&body=${encodeURIComponent(fullMessage)}`,
      color: 'hover:bg-slate-800 text-slate-400'
    }
  ];

  

  return (
    <div className="max-w-md mx-auto bg-slate-900 rounded-xl shadow-xl overflow-hidden p-6 border border-slate-800">

     <div className="flex flex-col items-center text-center mb-8">
  {/* Decorative Icon or Brand Element (Optional) */}
  <div className="w-12 h-12 bg-gradient-to-tr from-cyan-600 to-slate-800 rounded-2xl mb-4 flex items-center justify-center shadow-lg shadow-black/50 border border-cyan-500/30">
    <Share2 className="text-cyan-400 w-6 h-6" />
  </div>

  

  {
  message && (
    <div className="mb-6 w-full">
      <Alert
        variant={status === 'success' ? 'success' : 'error'}
        title={
          status === 'success'
            ? 'Success'
            : 'Invite Limit Error'
        }
        message={message}
        autoCloseMs={5000}
      />
    </div>
  )
}

  <h3 className="text-2xl font-extrabold text-slate-100 tracking-tight sm:text-3xl mb-2">
    Invite Friends
  </h3>

  {/* Status Badge */}
  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold tracking-wide transition-all">
    {expiryAt && !isNaN(new Date(expiryAt).getTime()) && new Date(expiryAt) > new Date() ? (
      <>
        {/* Active State */}
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400"></span>
        </span>
        <p className="bg-emerald-950/40 text-emerald-400 px-3 py-1 rounded-full border border-emerald-800/30">
          Link Active • Expires {new Date(expiryAt).toLocaleDateString()}
        </p>
      </>
    ) : (
      /* Expired State */
      <p className="bg-rose-950/40 text-rose-400 px-3 py-1 rounded-full border border-rose-800/30 flex items-center gap-1">
        <span className="text-lg">⚠️</span> Expired - Generate a new invite link to share again.
      </p>
    )}
  </div>
</div>

    {/* Invite Code Section */}
<div className="relative group overflow-hidden bg-gradient-to-br from-slate-950 to-slate-900 border border-slate-800 rounded-2xl p-4 mb-4 transition-all hover:shadow-lg hover:shadow-black/30">
  <div className="flex justify-between items-center relative z-10">
    <div>
      <p className="text-[10px] uppercase text-cyan-500 font-bold tracking-wider mb-1">Referral Code</p>
      <p className="text-2xl font-mono font-black text-cyan-400 tracking-tighter">
        {inviteCode}
      </p>
    </div>
    <button 
      onClick={() => {
        navigator.clipboard.writeText(inviteCode);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }}
      className="flex items-center justify-center w-10 h-10 bg-slate-950 shadow-sm border border-slate-800 rounded-xl hover:scale-110 active:scale-95 transition-all text-cyan-400 hover:border-cyan-500/30"
    >
      {copied ? <Check className="w-5 h-5 text-emerald-400" /> : <Copy className="w-5 h-5" />}
    </button>
  </div>
  {/* Decorative Background Glow */}
  <div className="absolute -right-4 -bottom-4 w-24 h-24 bg-cyan-500/5 rounded-full blur-2xl group-hover:bg-cyan-500/10 transition-all"></div>
</div>

{/* Invite Link Section */}
<div className="relative group overflow-hidden bg-gradient-to-br from-slate-950 to-slate-900 border border-slate-800 rounded-2xl p-4 mb-6 transition-all hover:shadow-lg hover:shadow-black/30">
  <div className="flex justify-between items-center relative z-10 gap-4">
    <div className="min-w-0 flex-1"> {/* min-w-0 prevents flex items from overflowing */}
      <p className="text-[10px] uppercase text-slate-500 font-bold tracking-wider mb-1">Direct Invite Link</p>
      <p className="text-sm font-medium text-slate-400 truncate break-all leading-tight">
        {inviteUrl}
      </p>
    </div>
    <button 
      onClick={copyToClipboard}
      className="flex-shrink-0 flex items-center justify-center w-10 h-10 bg-slate-950 shadow-sm border border-slate-800 rounded-xl hover:scale-110 active:scale-95 transition-all text-slate-400 hover:border-slate-700"
    >
      {copied ? <Check className="w-5 h-5 text-emerald-400" /> : <Copy className="w-5 h-5" />}
    </button>
  </div>
  {/* Decorative Background Glow */}
  <div className="absolute -right-4 -bottom-4 w-24 h-24 bg-slate-700/5 rounded-full blur-2xl group-hover:bg-slate-700/10 transition-all"></div>
</div>

      {/* Main Share Button (Desktop/Mobile) */}
      <button
        onClick={handleNativeShare}
        className="w-full mb-6 flex items-center justify-center gap-2 bg-cyan-600 hover:bg-cyan-700 border border-cyan-500/30 text-white font-semibold py-3 rounded-lg transition-colors shadow-lg shadow-cyan-950/50"
      >
        <Share2 size={20} />
        {/* {navigator.share ? 'Share Now' : 'Copy Link'} */}
        {'share' in navigator ? 'Share Now' : 'Copy Link'}
      </button>

      {/* Horizontal Social Icons (Manual Fallback) */}
      <div className="border-t border-slate-800 pt-6">
        <p className="text-center text-xs text-slate-500 mb-4 uppercase tracking-wider">Or share via</p>
        <div className="flex justify-around">
          {socials.map((social) => (
            <a
              key={social.name}
              href={social.link}
              target="_blank"
              rel="noopener noreferrer"
              className={`p-3 rounded-full transition-colors ${social.color}`}
              title={social.name}
            >
              {social.icon}
            </a>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SocialShareComponent;